import { useSelector } from '@datagrid/state';
import { useLocation } from 'react-router-dom';

import { TFPageHeader } from '@tf/shared';
import { Badge, Group } from '@tf/ui';
import { S, T } from '@tf/utils';

import { type ProfileGroups, useProfileGroupsSchemas } from '@/core/api/profiles';
import { useAccountParams, useEntityConnectionLabels, useSelectedAccount } from '@/core/hooks';
import { appStore } from '@/core/stores';
import { AccountIdEditor } from '@/components/accounts';
import { useProfileKindPossibleConnections } from '@/components/profiles/useProfileKindPossibleConnections';

import { AccountStructureButton } from './AccountStructureButton';
import { AccountTasksButton } from './AccountTasksButton';

const getAccountLabel = (accountType: string) => {
	switch (accountType) {
		case 'CORPORATE_ACCOUNT':
			return 'corporate';
		case 'INDIVIDUAL_ACCOUNT':
			return 'individual';
		case 'FUND_ACCOUNT':
			return 'fund';
		case 'SPC_FUND_ACCOUNT':
			return 'SPC fund';
		case 'INVESTMENT_FUND_ACCOUNT':
			return 'Investment fund';
		default:
			return accountType.split('_')[0];
	}
};

export const AccountHeader = () => {
	const { meta, reviews } = useSelectedAccount();
	const { connectionKind, entityId, accountId, connectionId } = useAccountParams();
	const location = useLocation();
	const { data: profileGroups } = useProfileGroupsSchemas();
	const tenant = useSelector(() => appStore.tenant?.get());
	T.assertNotNullish(tenant, 'no tenant in appStore');
	const accountType = meta.entityKind;
	const isEntity = location.pathname.includes('/entity/');
	const hasCasesInPath = location.pathname.includes('/cases');
	const hasPaymentsInPath = location.pathname.includes('/payments');
	const hasConnectionsInPath = location.pathname.includes('/connections');
	const basePath = `/accounts/${accountId}`;

	const getNextSegment = (keyword: string): string | null => {
		const pathSegments = location.pathname.split('/').filter(Boolean);
		const index = pathSegments.indexOf(keyword);
		return index !== -1 && index < pathSegments.length - 1 ? pathSegments[index + 1] : null;
	};

	const caseObjectNameParam = getNextSegment('cases');

	// TODO: update paymentId logic
	const paymentsId = getNextSegment('payments');

	let title = meta.name;

	const connectionLabels = useEntityConnectionLabels(meta.entityKind);
	const possibleConnections = useProfileKindPossibleConnections(meta.entityKind);

	const getBreadcrumbRoot = () => {
		if (!profileGroups?.groups) {
			return { title: 'Accounts', href: '/' };
		}

		const accountGroup = profileGroups.groups.find((group: ProfileGroups) =>
			group.kinds.includes(accountType)
		);

		if (accountGroup) {
			const isFirstGroup = profileGroups.groups[0] === accountGroup;
			const href = isFirstGroup ? '/' : `/${accountGroup.label.toLowerCase()}`;
			return { title: accountGroup.label, href };
		}

		const firstGroup = profileGroups.groups[0];
		return { title: firstGroup.label, href: '/' };
	};

	const breadcrumbRoot = getBreadcrumbRoot();
	const breadcrumbs: { title: string; href: string }[] = [
		breadcrumbRoot,
		{ title, href: `/accounts/${accountId}` },
	];
	if (isEntity) {
		const review = reviews.find((r) => r.graphLinkId == connectionId);
		const entity = review?.listEntities.find((e) => e.graphNodeId == entityId);
		title = entity?.name || 'Unknown';
		breadcrumbs.push(
			{
				href: `${basePath}/${S.slugify(connectionKind)}`,
				title: connectionLabels[connectionKind].plural,
			},
			{
				href: `${basePath}/${S.slugify(connectionKind)}/${connectionId}/entity/${entityId}`,
				title,
			}
		);
	} else if (connectionKind) {
		breadcrumbs.push({
			href: `${basePath}/${S.slugify(connectionKind)}`,
			title: connectionLabels[connectionKind].plural,
		});
	} else if (hasCasesInPath) {
		breadcrumbs.push({
			href: `/accounts/${accountId}/cases`,
			title: 'Cases',
		});
		if (caseObjectNameParam) {
			breadcrumbs.push({
				href: `/accounts/${accountId}/cases/${caseObjectNameParam}`,
				title: `Case`,
			});
		}
	} else if (hasConnectionsInPath) {
		breadcrumbs.push({
			href: `/accounts/${accountId}/connections`,
			title: possibleConnections.label,
		});
	} else if (hasPaymentsInPath) {
		breadcrumbs.push({
			href: `/accounts/${accountId}/payments`,
			title: 'Payments',
		});
		if (paymentsId) {
			breadcrumbs.push({
				href: `/accounts/${accountId}/payments/${paymentsId}`,
				title: `Payment ${paymentsId}`,
			});
		}
	}

	return (
		<TFPageHeader
			tenant={tenant}
			title={title}
			breadcrumbs={breadcrumbs}
			mb={0}
			badge={
				!isEntity ? (
					<Badge style={{ marginLeft: '0.5rem' }}>{getAccountLabel(accountType)}</Badge>
				) : undefined
			}
			toolbar={
				<Group gap={12}>
					<AccountTasksButton />
					<AccountStructureButton />
				</Group>
			}
			subtitle={<AccountIdEditor />}
		/>
	);
};
